import type { Rational, SectionTag, Basin, DelugeSnapshot, DelugeSliceState, VortexDelugeSnapshot } from './DelugeUtils.js';
import {
    rationalToNumber,
    sectionKeyToName,
    rationalToString
} from './DelugeUtils.js';

/** Format a vortex parent "sliceId:sectionKey" into readable form */
function formatVortexParent(parent: string | null): string {
    if (parent === null) return 'faucet';
    const colonIdx = parent.lastIndexOf(':');
    if (colonIdx < 0) return sectionKeyToName(parent);
    const sliceId = parent.slice(0, colonIdx);
    const sk = parent.slice(colonIdx + 1);
    const skName = sectionKeyToName(sk);
    if (sliceId === '') return skName;
    // Show the slice ancestry for cross-slice parents
    const ancestryNames = sliceId.split('|').map(ck => {
        const [r, c] = ck.split(',').map(Number);
        return String.fromCharCode(65 + c) + r;
    }).join(',');
    return `${skName}@[${ancestryNames}]`;
}

export class DelugeDebug {
    private timestamps: number[] = [];
    private snapshots: Map<number, DelugeSnapshot> = new Map();
    private vortexSnapshots: Map<number, VortexDelugeSnapshot> = new Map();
    private enabled: boolean = false;

    // Enable debug mode with specific timestamps to capture
    setTimestamps(timestamps: number[]): void {
        this.timestamps = [...timestamps].sort((a, b) => a - b);
        this.snapshots.clear();
        this.enabled = timestamps.length > 0;
    }

    isEnabled(): boolean {
        return this.enabled;
    }

    getTimestamps(): number[] {
        return this.timestamps;
    }

    clear(): void {
        this.snapshots.clear();
        this.vortexSnapshots.clear();
    }

    // Check if we should capture at the current time, and capture if so
    maybeCaptureSnapshot(
        currentTime: number,
        timestampIndex: number,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        flowState: Map<string, SectionTag>,
        basins: Basin[]
    ): number {
        if (!this.enabled) return timestampIndex;

        while (timestampIndex < this.timestamps.length &&
               this.timestamps[timestampIndex] <= currentTime) {
            this.captureSnapshot(
                this.timestamps[timestampIndex],
                filled,
                waterLevel,
                flowState,
                basins
            );
            timestampIndex++;
        }
        return timestampIndex;
    }

    private captureSnapshot(
        time: number,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        flowState: Map<string, SectionTag>,
        basins: Basin[]
    ): void {
        // Deep copy the state
        const filledCopy = new Set(filled);
        const waterLevelCopy = new Map<string, Rational>();
        for (const [k, v] of waterLevel) {
            waterLevelCopy.set(k, { ...v });
        }
        const flowStateCopy = new Map<string, SectionTag>();
        for (const [k, v] of flowState) {
            flowStateCopy.set(k, { ...v } as SectionTag);
        }
        const basinsCopy = basins.map(b => ({ sections: [...b.sections], rate: { ...b.rate } }));
        this.snapshots.set(time, {
            filled: filledCopy,
            waterLevel: waterLevelCopy,
            flowState: flowStateCopy,
            basins: basinsCopy
        });
    }

    // Log debug snapshots to console
    logSnapshots(): void {
        if (this.snapshots.size === 0) {
            console.log('No debug snapshots captured. Call setTimestamps() before simulation.');
            return;
        }
        console.log('\n=== Deluge Debug Snapshots ===');
        for (const [time, snapshot] of this.snapshots) {
            console.log(`\n--- t=${time} ---`);

            // Group flow state by type
            const drops: string[] = [];
            const crashes: string[] = [];
            const spreads: string[] = [];

            for (const [sk, tag] of snapshot.flowState) {
                const name = sectionKeyToName(sk);
                const parentName = tag.parent ? sectionKeyToName(tag.parent) : 'faucet';

                if (tag.type === 'drop') {
                    const rate = tag.rate ? rationalToString(tag.rate) : '?';
                    drops.push(`${name}(rate=${rate}, from=${parentName})`);
                } else if (tag.type === 'crash') {
                    const rate = rationalToString(tag.rate);
                    crashes.push(`${name}(rate=${rate}, from=${parentName})`);
                } else if (tag.type === 'spread') {
                    spreads.push(`${name}(from=${parentName})`);
                }
            }

            if (drops.length > 0) console.log(`  Drops: ${drops.join(', ')}`);
            if (crashes.length > 0) console.log(`  Crashes: ${crashes.join(', ')}`);
            if (spreads.length > 0) console.log(`  Spreads: ${spreads.join(', ')}`);

            // Show basins
            if (snapshot.basins.length > 0) {
                console.log(`  Basins:`);
                for (const basin of snapshot.basins) {
                    const sections = basin.sections.map(sk => sectionKeyToName(sk)).sort().join(', ');
                    const rate = rationalToString(basin.rate);
                    console.log(`    [rate=${rate}] ${sections}`);
                }
            }

            // Show filled sections
            const filledNames = [...snapshot.filled].map(sk => sectionKeyToName(sk)).sort();
            if (filledNames.length > 0) {
                console.log(`  Filled (${filledNames.length}): ${filledNames.join(', ')}`);
            }

            // Show partial fills
            const partials = [...snapshot.waterLevel.entries()]
                .filter(([sk]) => !snapshot.filled.has(sk))
                .map(([sk, level]) => `${sectionKeyToName(sk)}:${(rationalToNumber(level) * 100).toFixed(1)}%`)
                .sort();
            if (partials.length > 0) {
                console.log(`  Partial: ${partials.join(', ')}`);
            }
        }
        console.log('\n==============================\n');
    }

    // Get snapshots for programmatic access
    getSnapshots(): Map<number, DelugeSnapshot> {
        return this.snapshots;
    }

    // ── Vortex Snapshots ──────────────────────────────────────────

    // Check if we should capture a vortex snapshot at the current time
    maybeCaptureVortexSnapshot(
        currentTime: number,
        timestampIndex: number,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        slices: Map<string, DelugeSliceState>,
        basins: Basin[]
    ): number {
        if (!this.enabled) return timestampIndex;

        while (timestampIndex < this.timestamps.length &&
               this.timestamps[timestampIndex] <= currentTime) {
            this.captureVortexSnapshot(
                this.timestamps[timestampIndex],
                filled,
                waterLevel,
                slices,
                basins
            );
            timestampIndex++;
        }
        return timestampIndex;
    }

    private captureVortexSnapshot(
        time: number,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        slices: Map<string, DelugeSliceState>,
        basins: Basin[]
    ): void {
        const filledCopy = new Set(filled);
        const waterLevelCopy = new Map<string, Rational>();
        for (const [k, v] of waterLevel) {
            waterLevelCopy.set(k, { ...v });
        }
        const slicesCopy = new Map<string, DelugeSliceState>();
        for (const [id, slice] of slices) {
            const flowCopy = new Map<string, SectionTag>();
            for (const [k, v] of slice.flowState) {
                flowCopy.set(k, { ...v } as SectionTag);
            }
            slicesCopy.set(id, { ancestry: [...slice.ancestry], flowState: flowCopy });
        }
        const basinsCopy = basins.map(b => ({ sections: [...b.sections], rate: { ...b.rate } }));
        this.vortexSnapshots.set(time, {
            filled: filledCopy,
            waterLevel: waterLevelCopy,
            slices: slicesCopy,
            basins: basinsCopy
        });
    }

    // Log vortex snapshots grouped by slice
    logVortexSnapshots(): void {
        if (this.vortexSnapshots.size === 0) {
            console.log('No vortex debug snapshots captured. Call setTimestamps() before simulation.');
            return;
        }
        console.log('\n=== Deluge Vortex Debug Snapshots ===');
        for (const [time, snapshot] of this.vortexSnapshots) {
            console.log(`\n--- t=${time} ---`);

            // Display each slice
            for (const [sliceId, slice] of snapshot.slices) {
                const label = sliceId === ''
                    ? 'Slice [] (initial)'
                    : `Slice [${slice.ancestry.map(ck => {
                        const [r, c] = ck.split(',').map(Number);
                        return String.fromCharCode(65 + c) + r;
                    }).join(', ')}]`;

                const drops: string[] = [];
                const crashes: string[] = [];
                const spreads: string[] = [];

                for (const [sk, tag] of slice.flowState) {
                    const name = sectionKeyToName(sk);
                    const parentName = formatVortexParent(tag.parent);

                    if (tag.type === 'drop') {
                        const rate = tag.rate ? rationalToString(tag.rate) : '?';
                        drops.push(`${name}(rate=${rate}, from=${parentName})`);
                    } else if (tag.type === 'crash') {
                        const rate = rationalToString(tag.rate);
                        crashes.push(`${name}(rate=${rate}, from=${parentName})`);
                    } else if (tag.type === 'spread') {
                        spreads.push(`${name}(from=${parentName})`);
                    }
                }

                if (drops.length > 0 || crashes.length > 0 || spreads.length > 0) {
                    console.log(`  ${label}`);
                    if (drops.length > 0) console.log(`    Drops: ${drops.join(', ')}`);
                    if (crashes.length > 0) console.log(`    Crashes: ${crashes.join(', ')}`);
                    if (spreads.length > 0) console.log(`    Spreads: ${spreads.join(', ')}`);
                }
            }

            // Show basins
            if (snapshot.basins.length > 0) {
                console.log(`  Basins:`);
                for (const basin of snapshot.basins) {
                    const sections = basin.sections.map(sk => sectionKeyToName(sk)).sort().join(', ');
                    const rate = rationalToString(basin.rate);
                    console.log(`    [rate=${rate}] ${sections}`);
                }
            }

            // Show filled sections
            const filledNames = [...snapshot.filled].map(sk => sectionKeyToName(sk)).sort();
            if (filledNames.length > 0) {
                console.log(`  Filled (${filledNames.length}): ${filledNames.join(', ')}`);
            }

            // Show partial fills
            const partials = [...snapshot.waterLevel.entries()]
                .filter(([sk]) => !snapshot.filled.has(sk))
                .map(([sk, level]) => `${sectionKeyToName(sk)}:${(rationalToNumber(level) * 100).toFixed(1)}%`)
                .sort();
            if (partials.length > 0) {
                console.log(`  Partial: ${partials.join(', ')}`);
            }
        }
        console.log('\n==============================\n');
    }

    getVortexSnapshots(): Map<number, VortexDelugeSnapshot> {
        return this.vortexSnapshots;
    }
}
